import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowRight, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: '',
    website: '' // honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.service || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Frontend cooldown - 60 seconds between submissions
    const now = Date.now();
    if (now - lastSubmitTime < 60000) {
      const remaining = Math.ceil((60000 - (now - lastSubmitTime)) / 1000);
      toast.error(`Please wait ${remaining} seconds before sending another message.`);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          service: formData.service,
          message: formData.message.trim(),
          website: formData.website // honeypot
        }),
      });

      if (response.status === 429) {
        toast.error('Too many requests. Please try again later.');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send message');
      }

      const data = await response.json();
      toast.success(data.message || "Message sent! We'll get back to you within 24 hours.");
      setLastSubmitTime(Date.now());

      setFormData({
        name: '',
        email: '',
        service: '',
        message: '',
        website: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || 'Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />
      <div className="pt-32 pb-20">
        <section id="contact" className="py-20 relative" data-animate>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">Get In Touch</Badge>
                <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
                  Book a Discovery <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Call</span>
                </h1>
                <p className="text-xl text-slate-400">
                  Let's talk about transforming your brand's content strategy.
                </p>
                <a href="mailto:contact@phosstudio.net" className="inline-flex items-center gap-2 mt-4 text-amber-400 hover:text-amber-300 transition-colors font-semibold">
                  <Mail className="w-5 h-5" />
                  contact@phosstudio.net
                </a>
              </div>

              <Card className="bg-slate-900/50 border-amber-500/30 backdrop-blur-sm shadow-2xl shadow-amber-500/10 overflow-hidden">
                <CardContent className="p-0">
                  <iframe 
                    src="https://scheduler.zoom.us/phos-studio/discovery?embed=true"
                    className="w-full h-[700px] border-0 bg-transparent"
                    title="Schedule a Discovery Call"
                  ></iframe>
                </CardContent>
              </Card>

              <div className="text-center mb-12 mt-24">
                <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
                  Send Us a <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Message</span>
                </h2>
                <p className="text-xl text-slate-400">
                  Or leave us a message and we'll get back to you.
                </p>
              </div>

              <Card className="bg-slate-900/50 border-amber-500/30 backdrop-blur-sm shadow-2xl shadow-amber-500/10">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from users, bots will fill it */}
                    <div className="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <Label htmlFor="name" className="text-white font-semibold mb-2 block">Name</Label>
                      <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Your name" className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-white font-semibold mb-2 block">Email</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20" />
                    </div>
                    <div>
                      <Label htmlFor="service" className="text-white font-semibold mb-2 block">Service of Interest</Label>
                      <Select value={formData.service} onValueChange={handleServiceChange}>
                        <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white focus:border-amber-500 focus:ring-amber-500/20"><SelectValue placeholder="Select a service" /></SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 text-white">
                          <SelectItem value="social">Social Media Content & Ads</SelectItem>
                          <SelectItem value="editing">Video & Content Editing</SelectItem>
                          <SelectItem value="ugc">UGC Production</SelectItem>
                          <SelectItem value="all">All Services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-white font-semibold mb-2 block">Message</Label>
                      <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} placeholder="Tell us about your project..." rows={5} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20 resize-none" />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg py-6 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
